/*
  Studio in-checkout composer — order-time input collection on the AI-model-photo
  product page.  Pairs with sections/studio-composer.liquid + assets/studio-composer.css.

  FLOW
    Sign-in gate  → signed-out visitors see a "Sign in to start your order" button
                    linking to /pages/signin?return_url=<this product url>.
                    Signed-in customers get steps ①②.
    Step ① Upload → file input → POST /signed-url (Firebase-token gated) → PUT the
                    file to the returned GCS URL with the returned required headers
                    → GET /uploads for a display URL → ✓ Uploaded state + preview.
                    The object lands under customers/<uid>/uploads/, which is where
                    fulfillment resolves it from the customer's verified email.
    Step ② Pick    → visual radio grid of the ~roster models; stores the model NAME
                    (alt_parts[0], matching the backend registry).
    Add to Cart   → guests may add the $6 ai-model-photo line (card Mm7YTiiH)
                    AFTER they pick a model. Sign-in is required for composer
                    UPLOADS, not for ATC. A guest without a model is held with
                    the picker visible (CTO B1 option-1 — a gray require-sign-in
                    hold with the picker hidden was the measured defect). A
                    signed-in visitor is still held until photo + model are
                    set. Guest photo falls back to "reply to your confirmation
                    email". On add, the real product form carries:
                      properties[Model]        = chosen model name
                      properties[Source Photo] = uploaded filename (informational)
                      properties[Source image] / [Source image ref] = display URL +
                        durable GCS key (showroom DAKI5Sdf contract, so the
                        fulfillment identity bridge resolves it like a showroom upload)

  AUTH + UPLOAD are the theme's established pattern, reused verbatim:
    getApp() → getAuth() over the global firebase-auth.js app (studio-prod), the
    customer's own ID token via authedFetch() (studio-verify-email.js — one
    place mints the token, sets Authorization and retries once on the
    email_verified gate), and the byte-for-byte signed-URL contract used by
    studio-precheckout-upload.js / studio-composer-upload.js.
    No secret is embedded; the client only ever holds the customer's own token.

  Defensive: every hook is guarded and the module no-ops on any page without the
  composer or before firebase inits.
*/
import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth, onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { ThemeEvents } from '@theme/events';
import { authedFetch, renderStatus } from '@studio/verify-email';
import { gateableAddToCartButtons } from '@studio/buy-button-scope';

const composer = document.querySelector('[data-studio-composer]');

if (composer && getApps().length) {
  /*
    Y0fYDuh8 (Stage-1.5 blocking 2) — CLAIM ownership of properties[Model] /
    [Model handle] here, at the top of the live block, before anything can write
    a property.

    The channel used to answer "does [data-studio-composer] exist?" — ownership
    asserted by markup, which cannot know whether the owner is alive. Everything
    below this line, INCLUDING refreshGate()'s submit-disable, is behind the
    `composer && getApps().length` condition, so whenever the markup rendered and
    this module did not run (firebase not initialised, gstatic blocked, any
    throw) the capture script stood down, nobody wrote the property, and nobody
    gated the button: add-to-cart with NO Model at all.

    Reaching the claim proves the module is running. `claimedProps` mirrors it
    locally so syncProps() below can tell "my empty value" from "somebody
    else's correct value in a shared input".
  */
  const claimedProps = !!(window.StudioSelectedModel && window.StudioSelectedModel.claimOwnership());

  const BACKEND = (composer.getAttribute('data-backend-url') || '').replace(/\/$/, '');
  // EAPAHvZu — the sign-in URL has ONE source: snippets/studio-signin-url, rendered
  // into data-signin-url here and into window.__studioAuth in theme.liquid.
  // NB-1: snippets/studio-composer.liquid ALWAYS renders data-signin-url on this
  // element (it is not conditional), so the old ||-chain through __studioAuth and
  // a '/pages/signin' literal was unreachable. One last-resort literal is kept for
  // the case where the snippet is edited to drop the attribute.
  const SIGNIN_BASE = composer.getAttribute('data-signin-url') || '/pages/signin';

  // Funnel events → GA4 + PostHog + Mixpanel through the one emitter
  // (assets/studio-analytics.js, loaded before this module). Guarded: a page
  // without it simply emits nothing.
  const track = (name, props) => {
    try { window.StudioAnalytics && window.StudioAnalytics.track(name, props); } catch (_e) { /* GUARD-OK: analytics is best-effort telemetry; a failing sink must never block an upload or the buy button, and studio-analytics.js already isolates each sink. */ }
  };

  const gateEl     = composer.querySelector('[data-composer-gate]');
  const stepsEl    = composer.querySelector('[data-composer-steps]');
  const pickerEl   = composer.querySelector('[data-composer-picker]');
  const guestNoteEl = composer.querySelector('[data-composer-guest-photo-note]');
  const signinBtn  = composer.querySelector('[data-composer-signin]');
  const dropEl     = composer.querySelector('[data-composer-drop]');
  const inputEl    = composer.querySelector('[data-composer-input]');
  const labelEl    = composer.querySelector('[data-composer-label]');
  const statusEl   = composer.querySelector('[data-composer-status]');
  const previewEl  = composer.querySelector('[data-composer-preview]');
  const modelsEl   = composer.querySelector('[data-composer-models]');
  const propTpl    = composer.querySelector('[data-composer-property-template]');

  // The real product add-to-cart form. Horizon renders it as
  // form[action*="/cart/add"]; keep a resilient selector. Only the line-item
  // property inputs live here — the GATE no longer targets this form's button,
  // see addToCartTargets() below.
  const form =
    document.querySelector('form[action*="/cart/add"]') ||
    document.querySelector('form[data-type="add-to-cart-form"]');

  // EVERY add-to-cart control on the page, resolved LIVE on each refreshGate().
  //
  // Card XDwPLDax — a single cached `form.querySelector('[type=submit]')` was
  // wrong in two directions at once:
  //   1. The sticky bar's button (sections/product-information.liquid) sits
  //      OUTSIDE every form, so it was never gated. Its click handler puppets
  //      the in-form button via `.click()`, and a click on a DISABLED control
  //      dispatches nothing — so a customer who scrolled past the buy box (i.e.
  //      anyone who used the composer, which sits below it) got a silent no-op
  //      instead of a reason. Disabling the sticky button itself is the fix, and
  //      is why assets/sticky-add-to-cart.js needs no change.
  //   2. product-form.js morphs the add-to-cart button (and the whole
  //      .product-form-buttons container) on every variant change. A morph
  //      restores the SERVER-rendered attributes, so `disabled`/`title` were
  //      silently wiped and the cached node could be detached. Re-resolving per
  //      call — plus the variantUpdate listener at the bottom of this file —
  //      is what survives that.
  //
  // WHICH buttons: resolved through assets/studio-buy-button-scope.js, never
  // with a bare document-wide `.add-to-cart-button` query (card wAlneCl2).
  //
  // That class is also on snippets/quick-add.liquid's product-card buttons and on
  // the quick-add modal's morphed-in form, both of which buy a DIFFERENT
  // product. This file used to rely on `quick_add: false` in settings_data.json
  // to keep them out — a merchandising toggle nobody would think of as a guard.
  // Flipping it would have disabled Add to Cart on every product card on the
  // page, silently. The scope module excludes them by container, with a loud
  // fallback on browsers that support neither complex :not() nor closest().
  //
  // The module warns on a zero-target gate (an empty gate is an OPEN gate). This
  // runs on every refreshGate(), so each distinct warning is said ONCE.
  const warnedScope = new Set();
  function warnOnce(msg) {
    if (warnedScope.has(msg)) return;
    warnedScope.add(msg);
    console.warn(msg);
  }
  function addToCartTargets() {
    return gateableAddToCartButtons(document, warnOnce).buttons;
  }

  const auth = getAuth(getApp());
  const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
  const MAX_BYTES = 15 * 1024 * 1024;

  // --- i18n strings (mirrored from the section via data-* on window, with
  // English fallbacks so the script is robust if a string is missing). ---
  const T = window.StudioComposerStrings || {};
  const t = (k, fallback) => (T && T[k]) || fallback;

  const state = {
    signedIn: false,
    uploaded: null, // { ref, displayUrl, fileName }
    model: null,        // chosen model NAME  (properties[Model])
    modelHandle: '',    // canonical slug      (properties[Model handle])
    busy: false,
  };

  // uGqPRiwp: `status` is the HTTP status that produced `msg` (when there was
  // one). 403 = the backend's email_verified gate — the shared writer mounts
  // the resend control so the message is actionable instead of a dead end.
  const setStatus = (msg, isErr, status) => renderStatus(statusEl, msg, isErr, status);

  function signinHref() {
    // EAPAHvZu — compose through the ONE helper (layout/theme.liquid).
    const ret = window.location.pathname + window.location.search;
    return (window.__studioAuth && window.__studioAuth.withReturn)
      ? window.__studioAuth.withReturn(ret, SIGNIN_BASE)
      : SIGNIN_BASE + '?return_url=' + encodeURIComponent(ret);
  }

  // ----- Line-item property inputs, injected into the real cart form -----
  let propInputs = null;

  /*
    Y0fYDuh8 (Stage-1.5 blocking 3) — append only the inputs that are MISSING.

    This used to `form.appendChild(frag)` unconditionally and then bind via
    `form.querySelector`, which returns the FIRST match. When the channel asset
    404s (it is a brand-new file; a partial theme sync drops exactly this one)
    the capture script writes its own inputs, we appended a second
    properties[Model] and a second [Model handle], bound to the capture script's
    copies, and left our own template inputs at ''. The form then submitted two
    same-named inputs, one empty — and /cart/add's duplicate-key precedence is
    not ours to decide.

    Querying per input makes us ADOPT whatever is already there instead of
    racing it. Combined with setProp()'s no-clobber rule, the channel-missing
    fallback is now what the PR body claimed it was.
  */
  function ensurePropInputs() {
    if (propInputs || !form || !propTpl || !propTpl.content) return propInputs;
    const frag = propTpl.content.cloneNode(true);
    // Drop any templated input whose name is already present in the form.
    Array.from(frag.querySelectorAll('input[name]')).forEach((node) => {
      if (form.querySelector('input[name="' + CSS.escape(node.getAttribute('name')) + '"]')) {
        node.parentNode.removeChild(node);
      }
    });
    if (frag.childNodes.length) form.appendChild(frag);
    propInputs = {
      model: form.querySelector('input[name="properties[Model]"]'),
      modelHandle: form.querySelector('input[name="properties[Model handle]"]'),
      sourcePhoto: form.querySelector('input[name="properties[Source Photo]"]'),
      sourceImage: form.querySelector('input[name="properties[Source image]"]'),
      sourceRef: form.querySelector('input[name="properties[Source image ref]"]'),
    };
    assertSingleModelInput();
    return propInputs;
  }

  /*
    AC-5's second assertion, shipped: EXACTLY ONE properties[Model] input in the
    form that is about to be submitted. This is the literal predicate that would
    have caught blocking 3, and the static guard cannot express it — the count
    only exists at runtime.

    It REPAIRS rather than blocks. A duplicate means a non-deterministic Model on
    a paid order; refusing the sale would be a worse outcome than removing the
    input we know we do not own. The owner's binding is kept, extras are dropped,
    and it fails LOUDLY so the cause is visible in GA's exception stream.
  */
  function assertSingleModelInput() {
    if (!form) return true;
    let ok = true;
    ['properties[Model]', 'properties[Model handle]'].forEach((name) => {
      const all = Array.from(form.querySelectorAll('input[name="' + CSS.escape(name) + '"]'));
      if (all.length <= 1) return;
      ok = false;
      const keep = (propInputs && (name === 'properties[Model]' ? propInputs.model : propInputs.modelHandle)) || all[0];
      all.forEach((el) => { if (el !== keep && el.parentNode) el.parentNode.removeChild(el); });
      console.error(
        '[studio-composer] ' + all.length + ' `' + name + '` inputs in the cart form — ' +
        'duplicate line-item keys have no defined precedence at /cart/add. Kept one, removed ' +
        (all.length - 1) + '.'
      );
    });
    return ok;
  }

  /*
    Never blank a property we do not own. refreshGate() -> syncProps() runs on
    first paint with state.model === null, and when the channel is absent the
    input it is about to blank may hold studio-model-capture.js's correct value.
    Writing '' there was blocking 3's second half: the order lost its Model
    before the customer touched anything.
  */
  function setProp(input, value, owned) {
    if (!input) return;
    if (!value && !owned) return;
    input.value = value || '';
  }

  function syncProps() {
    const p = ensurePropInputs();
    if (!p) return;
    setProp(p.model, state.model, claimedProps);
    // Y0fYDuh8 — written by the SAME owner as [Model], from the same selection,
    // so the two can never disagree on a line item.
    setProp(p.modelHandle, state.modelHandle, claimedProps);
    // Composer-only properties: no other writer exists, so '' is ours to write.
    // Mm7YTiiH — a guest (no upload rail) who picked a model carries the
    // email-fallback photo instruction instead of an empty Source Photo.
    setProp(p.sourcePhoto, (state.uploaded && state.uploaded.fileName)
      || (!state.signedIn && state.model
        ? t('guest_photo_prop', 'Reply to confirmation email with product photo')
        : ''), true);
    setProp(p.sourceImage, (state.uploaded && state.uploaded.displayUrl) || '', true);
    setProp(p.sourceRef, (state.uploaded && state.uploaded.ref) || '', true);
  }

  // ----- Add-to-cart gating -----
  // Card Mm7YTiiH / CTO B1 option-1 — guest ATC is not require-sign-in, but
  // it is not a free add either. Guests must pick a model (picker is
  // reachable; Step ② lives outside data-composer-steps). Photo stays on
  // the email-fallback for guests; signed-in visitors still need photo +
  // model. Uploads stay Firebase-gated (handleFiles / drop).
  function refreshGate() {
    const ready = !state.busy && !!state.model && (!state.signedIn || !!state.uploaded);
    let title = '';
    if (!ready) {
      const reasons = [];
      if (state.signedIn && !state.uploaded) reasons.push(t('need_upload', 'upload your product photo'));
      if (!state.model) reasons.push(t('need_model', 'pick a model'));
      if (reasons.length) {
        title = t('disabled_prefix', 'To add to cart:') + ' ' + reasons.join(' & ') + '.';
      }
    }
    // EVERY target, every time — not the first match, and not a node cached
    // before the last morph.
    //
    // ── THE GATE IS ADDITIVE, NOT AUTHORITATIVE (card RfWYts6T) ─────────────
    // It may only clear a `disabled` IT set. Forcing `disabled = false` here
    // re-enables a button somebody else disabled for a different reason —
    // concretely: `product-form.js` disables Add to Cart on a sold-out variant
    // from its own section-level variant:update handler, and this listener is
    // on `document`, so it runs AFTER and undoes that.
    //
    // `data-studio-gated` is the ownership marker. Present ⇒ this gate is the
    // one holding the button down and may release it; absent ⇒ the `disabled`
    // belongs to another owner (stock, variant state, a future theme feature)
    // and is left exactly as found.
    //
    // Shopify rejects a sold-out add server-side, so the old behaviour cost a
    // confusing failure rather than a bad order — but the catalogue is closer
    // to that state than it looks: measured 2026-09-03, 15 of 24 variants
    // already carry `inventoryPolicy: DENY`, and the two tracked products sit
    // at quantity -1 and 0, held available only by `CONTINUE`. One ordinary
    // merchandising toggle reaches it.
    addToCartTargets().forEach((btn) => {
      if (!ready) {
        // ⚠️ ONLY claim a button that was FREE when we arrived. A closed gate
        // disables everything, so "we disabled it" and "it was already
        // disabled" are indistinguishable AFTER the write — the ownership
        // question can only be answered in the instant before it. A button
        // already held down by another owner is left unmarked, and therefore
        // never released by us.
        if (!btn.disabled) {
          btn.disabled = true;
          btn.setAttribute('data-studio-gated', 'true');
        }
      } else if (btn.getAttribute('data-studio-gated') === 'true') {
        // Ours to release.
        btn.disabled = false;
        btn.removeAttribute('data-studio-gated');
      }
      // `aria-disabled` mirrors the button's ACTUAL state, not this gate's
      // verdict — a screen reader must describe the button in front of the
      // user, and a button still held down by stock is still disabled even
      // once the composer is satisfied. The TITLE is the opposite: it carries
      // the composer's reason, so it is removed the moment that reason stops
      // being true, whoever else is holding the button.
      btn.setAttribute('aria-disabled', String(btn.disabled));
      if (title) btn.setAttribute('title', title);
      else btn.removeAttribute('title');
    });
    syncProps();
  }

  // ----- Firebase auth + signed-URL upload (theme-standard contract) -----

  // The statuses the theme LOCALIZES: each has an `err_<status>` key that
  // snippets/studio-composer.liquid bridges from locales/*.json through `| t`.
  // One table, so "which statuses have their own sentence" is decided once.
  const LOCALIZED_STATUS = {
    400: ['err_400', 'That file could not be accepted. Try a JPG, PNG or WebP under 15 MB.'],
    401: ['err_401', 'Your session expired — please sign in again.'],
    403: ['err_403', 'Verify your email to enable uploads.'],
    413: ['err_413', 'That file is too large. Max 15 MB.'],
    415: ['err_415', 'That file type is not supported. Use JPG, PNG or WebP.'],
    429: ['err_429', 'You have reached your upload limit.'],
  };

  async function api(path, opts) {
    // The token, the Authorization header and the retry-once on the
    // email_verified gate all live in authedFetch (studio-verify-email.js).
    const res = await authedFetch(BACKEND + path, opts);
    if (!res.ok) {
      const localized = LOCALIZED_STATUS[res.status];
      let msg = localized
        ? t(localized[0], localized[1])
        : t('err_generic', 'Upload failed') + ' (' + res.status + ')';
      // ⚠️ G19kIqwV B3 — the backend's own message may only IMPROVE the generic
      // fallback. It must never replace a status the theme localizes: the body
      // is English (`"Email not verified"` on a 403), and letting it win put an
      // English sentence above a Hebrew resend button on he.studio's P0 upload
      // surface. Measured by the ⭐ B3 test in scripts/studio-composer-gate.test.mjs.
      if (!localized) {
        try {
          const body = await res.clone().json();
          if (body && (body.error || body.message)) msg = body.error || body.message;
        } catch (_e) {
          // GUARD-OK: an error body that is not JSON is expected (a proxy or CDN
          // page). `msg` already holds the generic "<err_generic> (<status>)"
          // text set above, so the customer still gets a message — this only
          // declines to IMPROVE it.
        }
      }
      const e = new Error(msg); e.status = res.status; throw e;
    }
    return res.status === 204 ? null : res.json();
  }

  async function uploadOne(file) {
    // 1) scoped, size-bounded V4 signed PUT URL
    const signed = await api('/signed-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: file.type, size: file.size }),
    });
    // 2) PUT straight to GCS, echoing the exact signed headers (byte-for-byte
    //    contract: x-goog-content-length-range is baked into the V4 signature).
    const headers = Object.assign(
      { 'Content-Type': signed.contentType || file.type },
      signed.requiredHeaders || {}
    );
    const put = await fetch(signed.url, { method: 'PUT', headers, body: file });
    if (!put.ok) throw new Error(t('err_generic', 'Upload failed') + ' (' + put.status + ')');

    // 3) resolve a human-clickable display URL for the order (signed GET).
    let displayUrl = '';
    const ref = signed.gcsPath || '';
    try {
      const listing = await api('/uploads', { method: 'GET' });
      const match = (listing && listing.uploads || []).find((u) => u.gcsPath === signed.gcsPath);
      if (match) displayUrl = match.url;
    } catch (_e) {
      // GUARD-OK: the upload ALREADY SUCCEEDED — this listing call only fetches a
      // thumbnail URL for the preview. Losing it costs a picture, never the
      // order: `gcsPath` is what links the file to the line item.
      console.debug('[studio] upload preview URL unavailable', _e);
    }
    return { ref, displayUrl, fileName: file.name };
  }

  function renderUploaded(up) {
    if (!previewEl) return;
    previewEl.textContent = '';
    const wrap = document.createElement('div');
    wrap.className = 'studio-composer__thumb';
    if (up.displayUrl) {
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = up.displayUrl;
      img.alt = up.fileName || 'Your product photo';
      wrap.appendChild(img);
    }
    const cap = document.createElement('span');
    cap.className = 'studio-composer__thumb-name';
    cap.textContent = up.fileName || 'Product photo';
    wrap.appendChild(cap);
    previewEl.appendChild(wrap);
    previewEl.hidden = false;
    if (labelEl) labelEl.textContent = t('replace_cta', 'Replace photo');
  }

  async function handleFiles(files) {
    if (state.busy) return;
    const file = [...files].find((f) => ALLOWED_TYPES.has(f.type));
    if (!file) {
      if (files.length) setStatus(t('err_type', 'Use a JPG, PNG or WebP image.'), true);
      return;
    }
    if (file.size > MAX_BYTES) {
      setStatus(file.name + ' — ' + t('err_size', 'over the 15 MB limit.'), true);
      return;
    }
    if (!auth.currentUser) {
      setStatus(t('need_signin_upload', 'Sign in to upload your product photo.'), true);
      return;
    }
    state.busy = true;
    track('upload_started', { file_type: file.type, file_size_kb: Math.round(file.size / 1024) });
    setStatus(t('uploading', 'Uploading…'));
    if (dropEl) dropEl.classList.add('is-disabled');
    if (inputEl) inputEl.disabled = true;
    if (labelEl) labelEl.textContent = t('uploading', 'Uploading…');
    refreshGate();
    try {
      const up = await uploadOne(file);
      state.uploaded = up;
      track('upload_succeeded', { model_name: state.model || null });
      renderUploaded(up);
      setStatus(t('uploaded', '✓ Uploaded'));
    } catch (e) {
      // 403 here is the email_verified gate — the step the 2026-08-31 buy-path
      // audit found permanently blocks email/password customers (EIbXSOHs).
      track('upload_failed', {
        status: e.status || null,
        reason: e.status === 403 ? 'email_not_verified' : (e.status === 401 ? 'session_expired' : 'error'),
      });
      if (e.status === 401 || e.message === 'not-signed-in') {
        setStatus(t('err_401', 'Your session expired — please sign in again.'), true, 401);
      } else {
        setStatus(e.message, true, e.status);
      }
      if (labelEl) labelEl.textContent = state.uploaded ? t('replace_cta', 'Replace photo') : t('upload_cta', 'Upload product photo');
    } finally {
      state.busy = false;
      if (dropEl) dropEl.classList.remove('is-disabled');
      if (inputEl) inputEl.disabled = false;
      refreshGate();
    }
  }

  // ----- Model picker -----
  /*
    The ONE place a tile becomes the selected model (Stage-1.5 B2). The click
    handler and hydrateSelection() used to each set state.model / modelHandle /
    is-selected themselves — one reading `radio.value`, the other
    `data-model-name`. Equal today, and exactly how the two would drift apart
    without any test noticing. Both call this now.
  */
  function applyTile(tile, clickedRadio) {
    // The radio the customer actually clicked, when there is one; otherwise the
    // tile's own radio (hydration has no click to go on).
    const radio = clickedRadio || tile.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;
    modelsEl.querySelectorAll('[data-composer-model]').forEach((el) => {
      el.classList.toggle('is-selected', el === tile);
    });
    state.model = tile.getAttribute('data-model-name') || (radio && radio.value) || null;
    state.modelHandle = tile.getAttribute('data-model-handle') || '';
  }

  function wireModels() {
    if (!modelsEl) return;
    modelsEl.addEventListener('change', (e) => {
      const radio = e.target.closest('input[type="radio"]');
      if (!radio) return;
      const tileEl = radio.closest('[data-composer-model]');
      if (!tileEl) return;
      applyTile(tileEl, radio);
      track('model_selected', { model_name: state.model, model_handle: state.modelHandle || null, source: 'composer' });
      // Keep the shared channel in step, so any other surface reads the model the
      // customer actually has selected right now — one source of truth, both ways.
      if (state.model && window.StudioSelectedModel) {
        window.StudioSelectedModel.publish(state.model, state.modelHandle, 'composer');
      }
      refreshGate();
    });
  }

  // ----- File input wiring -----
  if (inputEl) {
    inputEl.addEventListener('change', (e) => { handleFiles(e.target.files); inputEl.value = ''; });
  }
  if (dropEl) {
    dropEl.addEventListener('dragover',  (e) => { e.preventDefault(); dropEl.classList.add('is-over'); });
    dropEl.addEventListener('dragleave', () => dropEl.classList.remove('is-over'));
    dropEl.addEventListener('drop', (e) => {
      e.preventDefault();
      dropEl.classList.remove('is-over');
      if (auth.currentUser) handleFiles(e.dataTransfer.files);
      else setStatus(t('need_signin_upload', 'Sign in to upload your product photo.'), true);
    });
  }

  if (signinBtn) {
    signinBtn.setAttribute('href', signinHref());
  }

  wireModels();

  // ----- Y0fYDuh8: hydrate from an already-captured selection ---------------
  // A customer who arrived via /products/ai-model-photo?model=<slug> has ALREADY
  // chosen. Before this, the readiness gate read only state.model, so it said
  // "pick a model" at someone who just had — and studio-model-capture.js wrote
  // its own properties[Model] onto the same form, so whichever script ran last
  // decided what we would actually render and ship.
  //
  // Match by HANDLE first (the canonical slug from snippets/model-slug.liquid,
  // now on every tile) and fall back to the display NAME, because a selection
  // published by a surface that does not know the slug carries handle: ''.
  function hydrateSelection(selection) {
    if (!selection || !selection.name) return;
    if (state.model === selection.name) return;   // already in step

    /*
      Y0fYDuh8 (Stage-1.5 blocking 1) — once the customer has picked a tile
      themselves, stop adopting. The channel already refuses a `capture`
      downgrade, so this is the belt to that brace: any future publisher at
      composer rank still cannot silently re-check a radio behind the customer's
      back, and a programmatic `radio.checked = true` fires no `change` event,
      which is what made the old revert TERMINAL.
    */
    const channel = window.StudioSelectedModel;
    if (channel && channel.hasInteracted && channel.hasInteracted() && selection.source !== 'composer') return;

    let tile = null;
    if (selection.handle && modelsEl) {
      tile = modelsEl.querySelector('[data-composer-model][data-model-handle="' + CSS.escape(selection.handle) + '"]');
    }
    if (!tile && modelsEl) {
      tile = Array.from(modelsEl.querySelectorAll('[data-composer-model]'))
        .find((el) => el.getAttribute('data-model-name') === selection.name) || null;
    }

    // No matching tile — a model the composer does not offer. Do NOT adopt it:
    // silently carrying an unofferable model into properties[Model] is the same
    // class of bug (an order for something the picker never showed).
    if (!tile) return;

    applyTile(tile);
    refreshGate();   // refreshGate() -> syncProps() writes both properties
  }

  // onSelected fires immediately when a selection already exists AND on every
  // later publish — studio-model-capture.js is a deferred CLASSIC script and
  // this is a module, so it has normally already run, but the ordering is not
  // guaranteed and a missed publish would put us straight back in the bug.
  if (window.StudioSelectedModel) {
    window.StudioSelectedModel.onSelected(hydrateSelection);
  } else {
    // ONE warning for ONE root cause. The missing channel has two consequences
    // and they used to be logged separately, which is just noise: we cannot
    // hydrate a captured selection, AND we did not claim ownership of the
    // properties — so studio-model-capture.js keeps writing them itself and
    // setProp() below will not blank its values. The order is still stamped;
    // the customer is asked to pick again, as before this card.
    console.warn(
      '[studio] StudioSelectedModel channel unavailable — composer cannot hydrate a captured selection ' +
      'and has NOT claimed properties[Model]; studio-model-capture.js keeps ownership'
    );
  }

  // ----- Model thumbnails: load on picker reveal, not lazily -----
  // The model tiles are rendered with loading="lazy". The picker is now
  // guest-visible (data-composer-picker), so thumbs can load on first paint.
  // Prime anyway: a leftover hidden ancestor, or a late morph, still leaves
  // lazy images at naturalWidth:0. Runs once.
  let thumbsPrimed = false;
  function primeModelThumbs() {
    if (thumbsPrimed || !modelsEl) return;
    thumbsPrimed = true;
    modelsEl.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      if (!img.complete || img.naturalWidth === 0) {
        const src = img.getAttribute('src');
        if (src) { img.removeAttribute('src'); img.setAttribute('src', src); }
      }
    });
  }

  // ----- Auth state → reveal / hide the UPLOAD step only -----
  // The model picker stays visible for guests (Mm7YTiiH B1). data-composer-steps
  // is upload-only and still hidden until Firebase says signed-in.
  onAuthStateChanged(auth, (user) => {
    state.signedIn = !!user;
    if (gateEl) gateEl.hidden = state.signedIn;
    if (stepsEl) stepsEl.hidden = !state.signedIn;
    if (pickerEl) pickerEl.hidden = false;
    if (guestNoteEl) guestNoteEl.hidden = state.signedIn;
    primeModelThumbs();
    if (!state.signedIn) {
      // Signed out mid-session: clear captured inputs so nothing stale rides the order.
      state.uploaded = null;
      state.model = null;
      state.modelHandle = '';
      if (previewEl) { previewEl.hidden = true; previewEl.textContent = ''; }
      if (labelEl) labelEl.textContent = t('upload_cta', 'Upload product photo');
      setStatus('');
      if (modelsEl) {
        modelsEl.querySelectorAll('input[type="radio"]').forEach((r) => { r.checked = false; });
        modelsEl.querySelectorAll('[data-composer-model]').forEach((tile) => tile.classList.remove('is-selected'));
      }
    }
    refreshGate();
  });

  // ----- Variant change → re-apply the gate -----
  // product-form.js morphs the add-to-cart button and .product-form-buttons in
  // its OWN `variant:update` handler, bound on `.closest('.shopify-section')`.
  // The event bubbles (see assets/events.js), so a document-level listener runs
  // after that section-level one — i.e. after the morph has already restored the
  // server-rendered attributes — and re-disables what the morph re-enabled.
  // ⚠️ Do NOT clear the ownership markers here before re-gating. It looks like
  // the right way to re-derive ownership at the one moment stock can change,
  // and it is measurably wrong: while the gate is CLOSED every button is
  // already `disabled`, so after a marker wipe the claim rule below (which only
  // claims a FREE button) claims nothing — and the gate can then never release
  // what it disabled. Measured: the buy button stays dead forever.
  //
  // Ownership is re-established by the MORPH instead, which is the mechanism
  // this file already documents: product-form.js re-renders the button from the
  // server on a variant change, and that wipes `disabled`, `title` and the
  // marker together. So a sold-out variant arrives here already `disabled` with
  // no marker — foreign, and correctly left alone.
  // Last line of defence: re-assert the single-input invariant at submit time.
  // A theme morph between first paint and the click can re-introduce a form
  // input we never saw, and this is the moment it would reach a paid order.
  if (form) form.addEventListener('submit', assertSingleModelInput);

  document.addEventListener(ThemeEvents.variantUpdate, refreshGate);

  // Initial gate state. Guests are held until they pick a model (picker is
  // visible); signed-in incomplete visitors stay held until photo + model.
  // The server already paints the buttons closed with need_model
  // (snippets/studio-composer-gate-attrs.liquid, SYKMBm5f AC-3), so this
  // call ADOPTS that hold rather than creating it — there is no open→held
  // window for a fast click to slip through (CTO #135 N1 / N5), and a page
  // where the module never runs stays closed.
  primeModelThumbs();
  refreshGate();
}
